/**
 * DeviceTypeModalContent - 共用 device type modal 渲染邏輯
 * 與 DeviceTypeModalPreview 一致，使用 trait/state/chart 元件 + FrameWrapper
 * 供 SmartHomeDashboardTemplate DeviceModal 與 DeviceTypeModalPreview 共用
 */
import { useMemo, useId, useEffect, useRef, useState, useCallback } from 'react'
import { getTraitStyleComponent, getStateStyleComponent } from '../../components/traitStyles'
import { getChartStyleComponent } from '../../components/datacharts/datachartStyleRegistry'
import {
  formatStateUpdatedAtLabel,
  getStateConfig,
  getStateMockValue,
  getStateUnit,
  normalizeStateLifecycle,
} from '../../components/traitStyles/traitStyleConfig'
import type { FramePreset } from '../../services/appfactoryFirestore'
import FrameWrapper from './FrameWrapper'
import { getDeviceTypeStyle01Traits, buildDefaultItemOrder } from './deviceTypeStyleConfig'
import { checkFirmwareUpdatesWithAuth, type Device, type AuthCreds } from '../../services/turingMcpApi'
import { buildScopedModalItemCss } from './cssValidation'

export type DeviceTypeModalContentConfig = {
  enabledTraits?: string[]
  enabledStates?: string[]
  enabledCharts?: string[]
  stateDisplayOverrides?: Record<string, { icon?: string; label?: string; unit?: string }>
  modalItemOrder?: string[]
  modalTraitStyles?: Record<string, string>
  modalStateStyles?: Record<string, string>
  modalChartStyles?: Record<string, string>
  modalItemVisible?: Record<string, boolean>
  modalItemSpacing?: Record<string, number>
  modalFrameStyles?: Record<string, FramePreset>
  modalItemCss?: Record<string, string>
}

export type DeviceTypeModalContentProps = {
  device: Device
  deviceState: Record<string, unknown>
  onCommand: (cmd: string, params?: Record<string, unknown>) => void | Promise<void>
  onClose: () => void
  deviceType: string
  config: DeviceTypeModalContentConfig
  /** 取得該 trait 的 attributes；runtime 用 device.attributes，preview 用 getMockAttributesForTrait */
  getAttributesForTrait: (traitId: string) => Record<string, unknown>
  /** 取得 state 的 mock 值（preview 用）；runtime 可傳 deviceState */
  getStateValue?: (stateKey: string) => unknown
  showFooterActions?: boolean
  /** 包裝模式：overlay=全螢幕 runtime，preview=402pt 預覽 */
  wrapMode: 'overlay' | 'preview'
  /** 供 CSS selector 使用的 style key（如 types-ac_unit-style01） */
  styleKey?: string
  modalBackground?: 'transparent' | string
  onPreviewItemContextMenu?: (payload: DeviceModalPreviewItemContextMenuPayload) => void
  editableDeviceNickname?: boolean
  onSaveDeviceNickname?: (nickname: string) => void | Promise<void>
  chartCredentials?: AuthCreds
  /** Runtime: hide items missing in real device capability/state */
  enforceCapabilityVisibility?: boolean
  /** Runtime control only: show debug device info button */
  showDeviceInfoButton?: boolean
}

export type DeviceModalPreviewItemContextMenuPayload = {
  itemKey: string
  itemKind: 'trait' | 'state' | 'chart'
  itemId: string
  itemStyleId: string
  slotKey: string
  slotLabel?: string
  slotState?: string
  clientX: number
  clientY: number
}

const DEFAULT_ITEM_SPACING = 20

function normalizeCapabilityToken(value: string): string {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')
}

function collectCapabilityStateKeys(
  deviceState: Record<string, unknown>,
  deviceAttributes: Record<string, unknown> | undefined
): Set<string> {
  const result = new Set<string>()
  for (const key of Object.keys(deviceState || {})) {
    const normalized = normalizeCapabilityToken(key)
    if (normalized) result.add(normalized)
  }
  const attrs = deviceAttributes && typeof deviceAttributes === 'object'
    ? deviceAttributes
    : {}
  for (const key of Object.keys(attrs)) {
    const normalized = normalizeCapabilityToken(key)
    if (normalized) result.add(normalized)
  }
  const sensorProfile = attrs.sensorProfile as Record<string, unknown> | undefined
  if (sensorProfile && typeof sensorProfile === 'object') {
    for (const key of Object.keys(sensorProfile)) {
      const normalized = normalizeCapabilityToken(key)
      if (normalized) result.add(normalized)
    }
  }
  return result
}

function unwrapStateDisplayValue(raw: unknown): unknown {
  if (Array.isArray(raw)) {
    if (raw.length === 0) return null
    return unwrapStateDisplayValue(raw[0])
  }
  if (raw && typeof raw === 'object') {
    const rec = raw as Record<string, unknown>
    if ('value' in rec) return unwrapStateDisplayValue(rec.value)
  }
  return raw
}

function toFiniteNumber(raw: unknown): number | null {
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw
  if (typeof raw === 'string') {
    const parsed = Number(raw)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

function isBatteryLikeDisplayState(
  stateKey: string,
  override: { icon?: string; label?: string; unit?: string } | undefined,
  unit: string
): boolean {
  const stateToken = normalizeCapabilityToken(stateKey)
  const labelToken = normalizeCapabilityToken(override?.label ?? '')
  const unitToken = String(unit || '').trim()
  return (
    stateToken.includes('battery') ||
    labelToken.includes('battery') ||
    (stateToken === 'power' && unitToken === '%')
  )
}

function isPowerLikeStateToken(token: string): boolean {
  return token === 'power' || token === 'realpower' || token === 'watt'
}

function hasBatteryStateSignal(deviceState: Record<string, unknown>): boolean {
  return Object.keys(deviceState || {}).some((key) => {
    const normalized = normalizeCapabilityToken(key)
    return normalized === 'battery' || normalized === 'batterylevel'
  })
}

function hasClimateTelemetrySignal(deviceState: Record<string, unknown>): boolean {
  const required = new Set([
    'temperature',
    'temp',
    'temperatureambientcelsius',
    'thermostattemperatureambient',
    'humidity',
    'rh',
    'humidityambientpercent',
    'relativehumidity',
  ])
  let hit = 0
  for (const rawKey of Object.keys(deviceState || {})) {
    const normalized = normalizeCapabilityToken(rawKey)
    if (!normalized) continue
    if (required.has(normalized)) {
      hit += 1
      if (hit >= 2) return true
    }
  }
  return false
}

function readStateByCandidateKey(
  deviceState: Record<string, unknown>,
  key: string
): unknown {
  if (key in deviceState) return deviceState[key]
  const normalizedTarget = normalizeCapabilityToken(key)
  if (!normalizedTarget) return undefined
  for (const [rawKey, value] of Object.entries(deviceState || {})) {
    if (normalizeCapabilityToken(rawKey) === normalizedTarget) return value
  }
  return undefined
}

function resolveRawStateValue(
  stateKey: string,
  getStateValue: ((stateKey: string) => unknown) | undefined,
  deviceState: Record<string, unknown>,
  override: { icon?: string; label?: string; unit?: string } | undefined
): unknown {
  const normalizedState = normalizeCapabilityToken(stateKey)
  const labelToken = normalizeCapabilityToken(override?.label ?? '')
  const unitToken = String(override?.unit || '').trim()
  const batteryAliasHint =
    labelToken.includes('battery') ||
    unitToken === '%' ||
    hasBatteryStateSignal(deviceState) ||
    hasClimateTelemetrySignal(deviceState)

  const candidates =
    isPowerLikeStateToken(normalizedState) && batteryAliasHint
      ? ['battery', 'batteryLevel', 'battery_level', 'batteryPercent', 'battery_percentage', 'power', 'realPower', 'real_power']
      : isPowerLikeStateToken(normalizedState)
        ? ['power', 'realPower', 'real_power', 'watt']
        : [stateKey]

  for (const candidate of candidates) {
    const fromGetter = getStateValue?.(candidate)
    if (fromGetter != null) return fromGetter
  }
  for (const candidate of candidates) {
    const fromRecord = readStateByCandidateKey(deviceState, candidate)
    if (fromRecord != null) return fromRecord
  }
  return getStateMockValue(stateKey)
}

function findOverrideByNormalizedKey(
  overrides: Record<string, { icon?: string; label?: string; unit?: string }>,
  normalizedTargetKey: string
): { icon?: string; label?: string; unit?: string } | undefined {
  for (const [rawKey, override] of Object.entries(overrides || {})) {
    if (normalizeCapabilityToken(rawKey) === normalizedTargetKey) return override
  }
  return undefined
}

function resolveStateDisplayOverride(
  stateKey: string,
  overrides: Record<string, { icon?: string; label?: string; unit?: string }>
): { icon?: string; label?: string; unit?: string } | undefined {
  const direct = overrides[stateKey]
  if (direct) return direct
  const normalized = normalizeCapabilityToken(stateKey)
  const sameKeyNormalized = findOverrideByNormalizedKey(overrides, normalized)
  if (sameKeyNormalized) return sameKeyNormalized

  // power 常被配置成 battery 的 alias；若編輯器存的是 battery override，這裡補上對應。
  if (isPowerLikeStateToken(normalized)) {
    const batteryOverride =
      findOverrideByNormalizedKey(overrides, 'battery') ??
      findOverrideByNormalizedKey(overrides, 'batterylevel')
    if (batteryOverride) return batteryOverride
  }
  return undefined
}

function shouldUseBatteryAliasPresentation(
  stateKey: string,
  rawValue: unknown,
  override: { icon?: string; label?: string; unit?: string } | undefined,
  defaultUnit: string,
  deviceState: Record<string, unknown>
): boolean {
  if (isBatteryLikeDisplayState(stateKey, override, defaultUnit)) return true
  const normalizedState = normalizeCapabilityToken(stateKey)
  if (!isPowerLikeStateToken(normalizedState)) return false

  const labelToken = normalizeCapabilityToken(override?.label ?? '')
  if (labelToken && !labelToken.includes('battery')) return false

  const explicitUnit = String(override?.unit || '').trim()
  if (explicitUnit && explicitUnit !== '%') return false

  const num = toFiniteNumber(unwrapStateDisplayValue(rawValue))
  if (num == null) return false
  if (num < 0 || num > 100) return false
  if (hasBatteryStateSignal(deviceState)) return true
  return hasClimateTelemetrySignal(deviceState)
}

function resolveDisplayStateValue(
  stateKey: string,
  rawValue: unknown,
  override: { icon?: string; label?: string; unit?: string } | undefined,
  unit: string
): unknown {
  const scalar = unwrapStateDisplayValue(rawValue)
  if (!isBatteryLikeDisplayState(stateKey, override, unit)) return scalar
  const num = toFiniteNumber(scalar)
  if (num == null) return scalar
  return Math.round(num)
}

function hasCustomModeGroup(
  deviceAttributes: Record<string, unknown> | undefined,
  groupName: string
): boolean {
  if (!deviceAttributes || typeof deviceAttributes !== 'object') return false
  const availableCustomModes = deviceAttributes.availableCustomModes as Array<{ name?: unknown }> | undefined
  if (!Array.isArray(availableCustomModes) || availableCustomModes.length === 0) return false
  const target = normalizeCapabilityToken(groupName)
  if (!target) return false
  return availableCustomModes.some((modeGroup) => {
    const modeName = normalizeCapabilityToken(String(modeGroup?.name ?? ''))
    return Boolean(modeName) && modeName === target
  })
}

function hasAvailableThermostatModes(deviceAttributes: Record<string, unknown> | undefined): boolean {
  if (!deviceAttributes || typeof deviceAttributes !== 'object') return false
  const raw = deviceAttributes.availableThermostatModes
  if (Array.isArray(raw)) return raw.length > 0
  if (typeof raw === 'string') {
    return raw
      .split(',')
      .map((mode) => mode.trim())
      .filter(Boolean)
      .length > 0
  }
  return false
}

const TRAIT_CAPABILITY_ALIAS: Record<string, string[]> = {
  onofftoggle: ['onoff'],
  startstop: ['onoff'],
  temperaturecontrol: ['temperaturesetting'],
}

const STATE_CAPABILITY_ALIAS: Record<string, string[]> = {
  temperature: ['temperature', 'temp', 'thermostattemperatureambient', 'measuredtemperature'],
  humidity: ['humidity', 'rh'],
  fanspeed: ['fanspeed', 'currentfanspeedsetting'],
  fan_speed: ['fanspeed', 'currentfanspeedsetting'],
  openclosepercent: ['openclosepercent', 'openpercent', 'position'],
  watt: ['watt', 'realpower', 'apparentpower', 'power'],
  watt_hour: ['watthour', 'energy', 'kwh'],
  brightness: ['brightness'],
  battery: ['battery', 'batterylevel'],
  occupancy: ['occupancy', 'presence'],
  motion: ['motion'],
  contact: ['contact', 'door'],
  co2: ['co2'],
  pm2_5: ['pm25', 'pm2_5'],
  lux: ['lux', 'illuminance'],
}

const CHART_REQUIRED_KEYS: Record<string, string[]> = {
  TemperatureChart: ['temperature', 'humidity'],
  TemperatureCard: ['temperature', 'humidity'],
  EnergyChart: ['watt', 'watt_hour'],
  EnergyCard: ['watt', 'watt_hour'],
  AirQualityChart: ['pm2_5', 'co2'],
  AirQualityCard: ['pm2_5', 'co2'],
  PresenceActivity: ['occupancy', 'motion'],
}

function mergeItemOrderWithEnabled(
  itemOrder: string[],
  enabledTraits: string[],
  enabledStates: string[],
  enabledCharts: string[]
): string[] {
  const traitSet = new Set(enabledTraits)
  const stateSet = new Set(enabledStates)
  const chartSet = new Set(enabledCharts)
  const seen = new Set<string>()
  const next: string[] = []
  for (const key of itemOrder) {
    const token = String(key || '').trim()
    if (!token || seen.has(token)) continue
    if (token.startsWith('trait:') && traitSet.size > 0 && !traitSet.has(token.slice(6))) continue
    if (token.startsWith('state:') && stateSet.size > 0 && !stateSet.has(token.slice(6))) continue
    if (token.startsWith('chart:') && chartSet.size > 0 && !chartSet.has(token.slice(6))) continue
    seen.add(token)
    next.push(token)
  }
  const appendIfMissing = (key: string) => {
    if (!seen.has(key)) {
      seen.add(key)
      next.push(key)
    }
  }
  for (const traitId of enabledTraits) appendIfMissing(`trait:${traitId}`)
  for (const stateKey of enabledStates) appendIfMissing(`state:${stateKey}`)
  for (const chartId of enabledCharts) appendIfMissing(`chart:${chartId}`)
  return next
}

function resolveUpdatedLabel(raw: unknown): string | undefined {
  if (raw == null) return undefined
  if (typeof raw === 'string') {
    const trimmed = raw.trim()
    if (!trimmed) return undefined
    const parsed = Date.parse(trimmed)
    if (Number.isNaN(parsed)) return trimmed
    const mins = Math.max(0, Math.round((Date.now() - parsed) / 60000))
    return formatStateUpdatedAtLabel(mins)
  }
  if (typeof raw === 'number') {
    if (!Number.isFinite(raw)) return undefined
    if (raw >= 0 && raw <= 240) return formatStateUpdatedAtLabel(raw)
    const ts = raw > 1e12 ? raw : raw * 1000
    const mins = Math.max(0, Math.round((Date.now() - ts) / 60000))
    return formatStateUpdatedAtLabel(mins)
  }
  return undefined
}

type DeviceInfoFieldRow = {
  key: string
  label: string
  value: string
  isNA: boolean
}

type FirmwareToastState = {
  message: string
  type: 'success' | 'error' | 'info'
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined
  return value as Record<string, unknown>
}

function getPathValue(source: Record<string, unknown> | undefined, path: string): unknown {
  if (!source || !path) return undefined
  const tokens = path.split('.').map((token) => token.trim()).filter(Boolean)
  if (tokens.length === 0) return undefined
  let cursor: unknown = source
  for (const token of tokens) {
    if (!cursor || typeof cursor !== 'object' || Array.isArray(cursor)) return undefined
    cursor = (cursor as Record<string, unknown>)[token]
  }
  return cursor
}

function pickFirstNonEmpty(
  sources: Array<Record<string, unknown> | undefined>,
  paths: string[]
): unknown {
  for (const source of sources) {
    for (const path of paths) {
      const value = getPathValue(source, path)
      if (value == null) continue
      if (typeof value === 'string' && !value.trim()) continue
      return value
    }
  }
  return undefined
}

function formatInfoValue(raw: unknown): { value: string; isNA: boolean } {
  if (raw == null) return { value: 'N/A', isNA: true }
  if (typeof raw === 'string') {
    const trimmed = raw.trim()
    return trimmed ? { value: trimmed, isNA: false } : { value: 'N/A', isNA: true }
  }
  if (typeof raw === 'number' || typeof raw === 'boolean') {
    return { value: String(raw), isNA: false }
  }
  if (Array.isArray(raw)) {
    const normalized = raw
      .map((item) => (item == null ? '' : String(item).trim()))
      .filter(Boolean)
      .join(', ')
    return normalized ? { value: normalized, isNA: false } : { value: 'N/A', isNA: true }
  }
  try {
    const serialized = JSON.stringify(raw)
    return serialized && serialized !== '{}' ? { value: serialized, isNA: false } : { value: 'N/A', isNA: true }
  } catch {
    return { value: String(raw), isNA: false }
  }
}

export default function DeviceTypeModalContent({
  device,
  deviceState,
  onCommand,
  onClose,
  deviceType,
  config,
  getAttributesForTrait,
  getStateValue,
  showFooterActions = false,
  wrapMode,
  styleKey,
  modalBackground,
  onPreviewItemContextMenu,
  editableDeviceNickname = false,
  onSaveDeviceNickname,
  chartCredentials,
  enforceCapabilityVisibility = false,
  showDeviceInfoButton = false,
}: DeviceTypeModalContentProps) {
  const {
    enabledTraits = [],
    enabledStates = [],
    enabledCharts = [],
    stateDisplayOverrides = {},
    modalItemOrder,
    modalTraitStyles: modalTraitStylesProp,
    modalStateStyles: modalStateStylesProp = {},
    modalChartStyles: modalChartStylesProp = {},
    modalItemVisible: modalItemVisibleProp,
    modalItemSpacing: modalItemSpacingProp,
    modalFrameStyles: modalFrameStylesProp,
    modalItemCss: modalItemCssProp,
  } = config

  const style01Traits = getDeviceTypeStyle01Traits(deviceType).map((t) => t.trait)
  const defaultTraitStyles = Object.fromEntries(
    (enabledTraits.length ? enabledTraits : style01Traits).map((t) => {
      const mapped = getDeviceTypeStyle01Traits(deviceType).find((x) => x.trait === t)
      return [t, mapped?.styleId ?? (t === 'TemperatureSetting' ? 'Style05' : 'Style01')]
    })
  )
  const modalTraitStyles = modalTraitStylesProp ?? defaultTraitStyles
  const modalStateStyles = modalStateStylesProp
  const modalChartStyles = modalChartStylesProp

  const configuredTraits =
    enabledTraits.length > 0
      ? enabledTraits
      : style01Traits.length > 0
        ? style01Traits
        : (modalItemOrder ?? [])
            .filter((key) => key.startsWith('trait:'))
            .map((key) => key.replace(/^trait:/, ''))
  const configuredStates =
    enabledStates.length > 0
      ? enabledStates
      : (modalItemOrder ?? [])
          .filter((key) => key.startsWith('state:'))
          .map((key) => key.replace(/^state:/, ''))
  const configuredCharts =
    enabledCharts.length > 0
      ? enabledCharts
      : (modalItemOrder ?? [])
          .filter((key) => key.startsWith('chart:'))
          .map((key) => key.replace(/^chart:/, ''))

  const capabilityFiltered = useMemo(() => {
    if (!enforceCapabilityVisibility) {
      return {
        traits: configuredTraits,
        states: configuredStates,
        charts: configuredCharts,
      }
    }

    const deviceTraitSet = new Set(
      (device.traits ?? [])
        .map((trait) => normalizeCapabilityToken(String(trait || '')))
        .filter(Boolean)
    )
    const stateKeySet = collectCapabilityStateKeys(deviceState, device.attributes)

    const hasTraitCapability = (traitId: string): boolean => {
      const normalized = normalizeCapabilityToken(traitId)
      if (!normalized) return false
      if (normalized === 'thermostatsetmode') {
        if (deviceTraitSet.has('thermostatsetmode')) return true
        const hasTemperatureTrait =
          deviceTraitSet.has('temperaturesetting') || deviceTraitSet.has('temperaturecontrol')
        return hasTemperatureTrait && hasAvailableThermostatModes(device.attributes)
      }
      if (deviceTraitSet.has(normalized)) return true
      const aliases = TRAIT_CAPABILITY_ALIAS[normalized] ?? []
      return aliases.some((alias) => deviceTraitSet.has(alias))
    }

    const hasStateCapability = (stateKey: string): boolean => {
      const normalized = normalizeCapabilityToken(stateKey)
      if (!normalized) return false
      if (stateKeySet.has(normalized)) return true
      const aliases = STATE_CAPABILITY_ALIAS[stateKey] ?? STATE_CAPABILITY_ALIAS[normalized] ?? []
      return aliases.some((alias) => stateKeySet.has(normalizeCapabilityToken(alias)))
    }

    const customModesContainsFanSpeed =
      hasTraitCapability('CustomModes') && hasCustomModeGroup(device.attributes, 'FanSpeed')
    const filteredTraits = configuredTraits.filter((traitId) => {
      const normalizedTrait = normalizeCapabilityToken(traitId)
      if (customModesContainsFanSpeed && normalizedTrait === 'fanspeed') return false
      return hasTraitCapability(traitId)
    })
    const filteredStates = configuredStates.filter((stateKey) => {
      if (!hasStateCapability(stateKey)) return false
      const normalizedStateKey = normalizeCapabilityToken(stateKey)
      const thermostatModeState = normalizedStateKey === 'thermostatmode'
      if (!thermostatModeState) return true
      const hasThermostatSetModeTrait = filteredTraits.some(
        (traitId) => normalizeCapabilityToken(traitId) === 'thermostatsetmode'
      )
      return !hasThermostatSetModeTrait
    })
    const filteredCharts = configuredCharts.filter((chartId) => {
      const requiredStateKeys = CHART_REQUIRED_KEYS[chartId]
      if (!requiredStateKeys || requiredStateKeys.length === 0) return true
      return requiredStateKeys.some((stateKey) => hasStateCapability(stateKey))
    })

    return {
      traits: filteredTraits,
      states: filteredStates,
      charts: filteredCharts,
    }
  }, [
    configuredCharts,
    configuredStates,
    configuredTraits,
    device.attributes,
    device.traits,
    deviceState,
    enforceCapabilityVisibility,
  ])

  const traits = capabilityFiltered.traits
  const states = capabilityFiltered.states
  const charts = capabilityFiltered.charts
  const thermostatModeControlAvailable = useMemo(() => {
    const traitSet = new Set(
      (device.traits ?? [])
        .map((traitId) => normalizeCapabilityToken(String(traitId || '')))
        .filter(Boolean)
    )
    if (hasAvailableThermostatModes(device.attributes)) return true
    if (traitSet.has('thermostatsetmode')) return true
    const hasTemperatureTrait =
      traitSet.has('temperaturesetting') || traitSet.has('temperaturecontrol')
    return hasTemperatureTrait && Boolean(deviceState.thermostatMode ?? deviceState.mode)
  }, [device.attributes, device.traits, deviceState.mode, deviceState.thermostatMode])
  const thermostatSetModeTraitVisible = useMemo(
    () => traits.some((traitId) => normalizeCapabilityToken(traitId) === 'thermostatsetmode'),
    [traits]
  )

  const itemOrderBase =
    modalItemOrder?.length > 0
      ? modalItemOrder
      : buildDefaultItemOrder(traits, states, charts)
  const itemOrder = useMemo(
    () => mergeItemOrderWithEnabled(itemOrderBase, traits, states, charts),
    [itemOrderBase, traits, states, charts]
  )

  const getItemSpacing = (itemKey: string) =>
    modalItemSpacingProp?.[itemKey] ?? DEFAULT_ITEM_SPACING
  const getItemFramePreset = (itemKey: string): FramePreset =>
    modalFrameStylesProp?.[itemKey] ?? 'default'
  const isItemVisible = (key: string) => modalItemVisibleProp?.[key] !== false

  const controlItems = itemOrder
    .filter((key) => {
      if (!isItemVisible(key)) return false
      if (key.startsWith('trait:')) return traits.includes(key.slice(6))
      if (key.startsWith('state:')) return states.includes(key.slice(6))
      if (key.startsWith('chart:')) return charts.includes(key.slice(6))
      return false
    })
    .map((key) => {
      if (key.startsWith('trait:')) {
        const traitId = key.slice(6)
        const traitStyleId = modalTraitStyles[traitId] ?? 'Style01'
        const TraitComponent = getTraitStyleComponent(traitId, traitStyleId)
        return TraitComponent
          ? {
              type: 'trait' as const,
              key: traitId,
              itemStyleId: traitStyleId,
              itemKey: key,
              el: (
                <TraitComponent
                  key={key}
                  trait={traitId}
                  device={device}
                  state={deviceState}
                  attributes={getAttributesForTrait(traitId)}
                  onCommand={onCommand}
                />
              ),
            }
          : null
      }
      if (key.startsWith('state:')) {
        const stateKey = key.slice(6)
        const normalizedStateKey = normalizeCapabilityToken(stateKey)
        const thermostatModeState = normalizedStateKey === 'thermostatmode'
        if (thermostatModeState && thermostatModeControlAvailable) {
          if (thermostatSetModeTraitVisible) return null
          const thermostatTraitStyleId = modalTraitStyles.ThermostatSetMode ?? 'Style01'
          const ThermostatModeTraitComponent = getTraitStyleComponent('ThermostatSetMode', thermostatTraitStyleId)
          if (ThermostatModeTraitComponent) {
            return {
              type: 'trait' as const,
              key: 'ThermostatSetMode',
              itemStyleId: thermostatTraitStyleId,
              itemKey: key,
              el: (
                <ThermostatModeTraitComponent
                  key={key}
                  trait="ThermostatSetMode"
                  device={device}
                  state={deviceState}
                  attributes={getAttributesForTrait('ThermostatSetMode')}
                  onCommand={onCommand}
                />
              ),
            }
          }
        }
        const stateStyleId = modalStateStyles[stateKey] ?? 'Style01'
        const StateComponent = getStateStyleComponent(stateKey, stateStyleId)
        const displayOverride = resolveStateDisplayOverride(stateKey, stateDisplayOverrides)
        const unit = getStateUnit(stateKey, displayOverride)
        const rawValue = resolveRawStateValue(stateKey, getStateValue, deviceState, displayOverride)
        const batteryLike = shouldUseBatteryAliasPresentation(stateKey, rawValue, displayOverride, unit, deviceState)
        const displayStateKey = batteryLike ? 'battery' : stateKey
        const stateConfig = getStateConfig(displayStateKey, displayOverride)
        const displayUnit = batteryLike ? '%' : unit
        const value = resolveDisplayStateValue(displayStateKey, rawValue, displayOverride, displayUnit)
        const precision = batteryLike ? 0 : undefined
        const lifecycleRaw =
          deviceState[`${stateKey}_lifecycle`] ??
          deviceState[`${stateKey}Lifecycle`] ??
          (deviceState.online === false ? 'offline' : null)
        const updatedAtLabel = resolveUpdatedLabel(
          deviceState[`${stateKey}_updated_at`] ??
            deviceState[`${stateKey}UpdatedAt`] ??
            deviceState[`${stateKey}_ts`] ??
            deviceState[`${stateKey}Ts`]
        )
        return StateComponent
          ? {
              type: 'state' as const,
              key: stateKey,
              itemStyleId: stateStyleId,
              itemKey: key,
              el: (
                <StateComponent
                  key={key}
                  stateKey={displayStateKey}
                  value={value}
                  unit={displayUnit}
                  label={stateConfig.label}
                  icon={stateConfig.icon}
                  precision={precision}
                  lifecycle={lifecycleRaw != null ? normalizeStateLifecycle(lifecycleRaw) : undefined}
                  updatedAtLabel={updatedAtLabel}
                  thresholdEnabled
                />
              ),
            }
          : null
      }
      const chartId = key.slice(6)
      const chartStyleId = modalChartStyles[chartId] ?? 'Style01'
      const ChartComponent = getChartStyleComponent(chartId, chartStyleId)
      return ChartComponent
        ? {
            type: 'chart' as const,
            key: chartId,
            itemStyleId: chartStyleId,
            itemKey: key,
            el: (
              <ChartComponent
                key={key}
                deviceSN={String(device.deviceSN ?? device.sn ?? '').trim() || null}
                credentials={chartCredentials}
              />
            ),
          }
        : null
    })
    .filter((x): x is NonNullable<typeof x> => x != null)

  const deviceName =
    (device.nickname && String(device.nickname).trim()) ||
    device.displayName ||
    device.name ||
    device.sn ||
    device.deviceSN ||
    'Device'
  const [displayDeviceName, setDisplayDeviceName] = useState(deviceName)
  const [nicknameEditing, setNicknameEditing] = useState(false)
  const [nicknameInput, setNicknameInput] = useState(deviceName)
  const [nicknameSaving, setNicknameSaving] = useState(false)
  const [nicknameError, setNicknameError] = useState<string | null>(null)
  const [deviceInfoOpen, setDeviceInfoOpen] = useState(false)
  const [deviceSnCopied, setDeviceSnCopied] = useState(false)
  const [firmwareChecking, setFirmwareChecking] = useState(false)
  const [firmwareToast, setFirmwareToast] = useState<FirmwareToastState | null>(null)
  const firmwareToastTimerRef = useRef<number | null>(null)
  const nicknameEditRootRef = useRef<HTMLDivElement | null>(null)
  const nicknameEditToggleRef = useRef<HTMLButtonElement | null>(null)

  const firmwareAuth = useMemo<AuthCreds | null>(() => {
    const appId = String(chartCredentials?.appId || '').trim()
    if (!appId) return null
    const accessToken = String(chartCredentials?.accessToken || '').trim()
    if (accessToken) return { accessToken, appId }
    const apiKey = String(chartCredentials?.apiKey || '').trim()
    if (apiKey) return { apiKey, appId }
    return null
  }, [chartCredentials])

  const firmwareCheckTarget = useMemo(() => {
    const deviceRecord = asRecord(device)
    const systemRecord = asRecord(deviceRecord?.system)
    const normalizeSn = (value: unknown): string => String(value || '').trim().split('#')[0] || ''
    const sn = normalizeSn(
      systemRecord?.baseSN
      ?? deviceRecord?.baseSN
      ?? deviceRecord?.sn
      ?? deviceRecord?.deviceSN
    )
    const groupId = String(systemRecord?.groupId ?? deviceRecord?.groupId ?? '').trim()
    return {
      sn: sn || undefined,
      groupId: groupId || undefined,
    }
  }, [device])

  const canCheckFirmware = Boolean(
    firmwareAuth &&
    (firmwareCheckTarget.sn || firmwareCheckTarget.groupId)
  )

  const canShowDeviceInfoButton = showDeviceInfoButton && editableDeviceNickname
  const deviceInfoRows = useMemo<DeviceInfoFieldRow[]>(() => {
    const attrs = asRecord(device.attributes)
    const caps = asRecord(device.capabilities)
    const state = asRecord(deviceState)
    const deviceRecord = asRecord(device)
    const sources = [attrs, caps, state, deviceRecord]
    const toRow = (key: string, label: string, value: unknown): DeviceInfoFieldRow => {
      const normalized = formatInfoValue(value)
      return {
        key,
        label,
        value: normalized.value,
        isNA: normalized.isNA,
      }
    }
    return [
      toRow(
        'deviceType',
        'Device Type',
        pickFirstNonEmpty(sources, [
          'canonicalDeviceType',
          'canonical_device_type',
          'system.canonicalDeviceType',
          'system.canonical_device_type',
          'deviceType',
          'type',
        ])
      ),
      toRow(
        'ipAddress',
        'IP Address',
        pickFirstNonEmpty(sources, ['ip', 'ipAddress', 'ip_address', 'localIp', 'local_ip', 'system.ip'])
      ),
      toRow(
        'firmwareVersion',
        'FW Version',
        pickFirstNonEmpty(sources, [
          'firmwareVersion',
          'firmware_version',
          'fwVersion',
          'fw_version',
          'firmware',
          'ver.fwVer',
          'ver.otaFwVer',
          'system.ver.fwVer',
          'system.ver.otaFwVer',
          'system.firmwareVersion',
          'system.fwVersion',
          'system.firmware',
          'system.softwareVersion',
        ])
      ),
      toRow(
        'rssi',
        'RSSI',
        pickFirstNonEmpty(sources, ['rssi', 'wifiRssi', 'wifi_rssi', 'system.rssi'])
      ),
      toRow(
        'uplinkSsid',
        'Uplink SSID',
        pickFirstNonEmpty(sources, [
          'uplink_ssid',
          'uplinkSsid',
          'ssid',
          'wifiSsid',
          'system.uplink_ssid',
          'system.uplinkSsid',
          'system.uplink_bssid',
          'system.uplinkBssid',
        ])
      ),
      toRow(
        'deviceSN',
        'Device SN',
        pickFirstNonEmpty(sources, ['deviceSN', 'sn', 'system.deviceSN', 'system.sn'])
      ),
    ]
  }, [device, deviceState])

  useEffect(() => {
    if (!nicknameEditing) {
      setDisplayDeviceName(deviceName)
      setNicknameInput(deviceName)
      setNicknameError(null)
    }
  }, [deviceName, nicknameEditing])

  const cancelNicknameEditing = useCallback(() => {
    setNicknameEditing(false)
    setNicknameInput(displayDeviceName)
    setNicknameError(null)
  }, [displayDeviceName])

  useEffect(() => {
    if (!nicknameEditing) return
    const handleOutsidePointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null
      if (!target) return
      if (nicknameEditRootRef.current?.contains(target)) return
      if (nicknameEditToggleRef.current?.contains(target)) return
      cancelNicknameEditing()
    }
    document.addEventListener('pointerdown', handleOutsidePointerDown)
    return () => document.removeEventListener('pointerdown', handleOutsidePointerDown)
  }, [nicknameEditing, cancelNicknameEditing])

  useEffect(() => {
    if (!canShowDeviceInfoButton && deviceInfoOpen) {
      setDeviceInfoOpen(false)
    }
  }, [canShowDeviceInfoButton, deviceInfoOpen])

  useEffect(() => {
    if (!deviceInfoOpen) return
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setDeviceInfoOpen(false)
      }
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [deviceInfoOpen])

  useEffect(() => {
    if (!deviceInfoOpen && deviceSnCopied) {
      setDeviceSnCopied(false)
    }
  }, [deviceInfoOpen, deviceSnCopied])

  useEffect(() => {
    if (deviceInfoOpen) return
    if (firmwareToast) setFirmwareToast(null)
  }, [deviceInfoOpen, firmwareToast])

  useEffect(() => {
    return () => {
      if (firmwareToastTimerRef.current != null) {
        window.clearTimeout(firmwareToastTimerRef.current)
      }
    }
  }, [])

  const handleCopyDeviceSn = useCallback(async (value: string) => {
    const text = String(value || '').trim()
    if (!text || text === 'N/A') return
    try {
      if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
        await navigator.clipboard.writeText(text)
      } else {
        const textarea = document.createElement('textarea')
        textarea.value = text
        textarea.setAttribute('readonly', '')
        textarea.style.position = 'fixed'
        textarea.style.opacity = '0'
        textarea.style.pointerEvents = 'none'
        document.body.appendChild(textarea)
        textarea.select()
        document.execCommand('copy')
        document.body.removeChild(textarea)
      }
      setDeviceSnCopied(true)
      window.setTimeout(() => setDeviceSnCopied(false), 1400)
    } catch {
      setDeviceSnCopied(false)
    }
  }, [])

  const showFirmwareToast = useCallback((message: string, type: FirmwareToastState['type']) => {
    if (firmwareToastTimerRef.current != null) {
      window.clearTimeout(firmwareToastTimerRef.current)
      firmwareToastTimerRef.current = null
    }
    setFirmwareToast({ message, type })
    firmwareToastTimerRef.current = window.setTimeout(() => {
      setFirmwareToast(null)
      firmwareToastTimerRef.current = null
    }, 1800)
  }, [])

  const handleCheckFirmware = useCallback(async () => {
    if (firmwareChecking) return
    if (!firmwareAuth) {
      showFirmwareToast('目前無法檢查韌體（缺少認證）', 'error')
      return
    }
    if (!firmwareCheckTarget.sn && !firmwareCheckTarget.groupId) {
      showFirmwareToast('目前無法檢查韌體（缺少裝置資訊）', 'error')
      return
    }
    setFirmwareChecking(true)
    try {
      const result = await checkFirmwareUpdatesWithAuth(firmwareAuth, {
        sn: firmwareCheckTarget.sn,
        groupId: firmwareCheckTarget.groupId,
        getFirmwareURL: false,
      })
      if (!result.success) {
        showFirmwareToast(result.error || '韌體檢查失敗', 'error')
        return
      }
      if (!result.hasUpdate) {
        showFirmwareToast('目前就已經是最新的', 'info')
        return
      }
      showFirmwareToast(
        result.latestVersion
          ? `偵測到可更新版本：${result.latestVersion}`
          : '偵測到可更新版本',
        'success'
      )
    } finally {
      setFirmwareChecking(false)
    }
  }, [firmwareAuth, firmwareCheckTarget.groupId, firmwareCheckTarget.sn, firmwareChecking, showFirmwareToast])

  const handleNicknameSave = useCallback(async () => {
    if (nicknameSaving) return
    const next = String(nicknameInput || '').trim()
    if (!next) {
      setNicknameError('Nickname cannot be empty')
      return
    }
    if (next === displayDeviceName) {
      setNicknameEditing(false)
      setNicknameError(null)
      return
    }
    setNicknameError(null)
    setNicknameSaving(true)
    try {
      if (onSaveDeviceNickname) {
        await onSaveDeviceNickname(next)
      }
      setDisplayDeviceName(next)
      setNicknameEditing(false)
    } catch (error) {
      setNicknameError(error instanceof Error ? error.message : 'Failed to save nickname')
    } finally {
      setNicknameSaving(false)
    }
  }, [nicknameInput, displayDeviceName, nicknameSaving, onSaveDeviceNickname])

  const modalStyle =
    modalBackground === 'transparent'
      ? { background: 'transparent' }
      : modalBackground
        ? { background: modalBackground }
        : undefined

  const normalizeCssToken = (value: string): string => {
    const s = String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
    return s || 'unknown'
  }
  const normalizedDeviceType = normalizeCssToken(deviceType)
  const normalizedStyleKey = normalizeCssToken(styleKey || 'trait-combo')
  const modalInstanceId = useId()
  const modalRootSelector = `.appfactory-device-modal[data-modal-instance="${modalInstanceId}"]`
  const scopedItemCss = useMemo(
    () => buildScopedModalItemCss(modalItemCssProp, modalRootSelector),
    [modalItemCssProp, modalRootSelector]
  )
  const modalClassName = [
    wrapMode === 'overlay' ? '' : 'device-type-modal-preview-modal-wrap',
    'smart-home-device-modal',
    'glass',
    'appfactory-device-modal',
    `appfactory-device-modal--${wrapMode}`,
    `appfactory-device-modal-type-${normalizedDeviceType}`,
    `appfactory-device-modal-style-${normalizedStyleKey}`,
  ]
    .filter(Boolean)
    .join(' ')

  const modalInner = (
    <div
      className={modalClassName}
      data-wrap-mode={wrapMode}
      data-device-type={normalizedDeviceType}
      data-modal-style-key={normalizedStyleKey}
      data-modal-instance={modalInstanceId}
      style={modalStyle}
      onClick={(e) => e.stopPropagation()}
    >
      {scopedItemCss && (
        <style
          data-appfactory-modal-item-css
          dangerouslySetInnerHTML={{ __html: scopedItemCss }}
        />
      )}
      <div className="smart-home-device-modal-header appfactory-device-modal__header">
        <button
          type="button"
          className="smart-home-device-modal-back-btn appfactory-device-modal__back"
          onClick={onClose}
          aria-label="Back"
          title="Back"
        >
          <span className="material-symbols-rounded">arrow_back</span>
        </button>
        {!editableDeviceNickname && (
          <h2 className="smart-home-automation-title appfactory-device-modal__title">{displayDeviceName}</h2>
        )}
        {editableDeviceNickname && (
          <div className="appfactory-device-nickname-wrap">
            {!nicknameEditing && (
              <div className="appfactory-device-nickname-view">
                <h2 className="smart-home-automation-title appfactory-device-modal__title">{displayDeviceName}</h2>
                <div className="appfactory-device-nickname-actions">
                  <button
                    ref={nicknameEditToggleRef}
                    type="button"
                    className="appfactory-device-nickname-edit-btn"
                    onClick={() => {
                      setNicknameInput(displayDeviceName)
                      setNicknameError(null)
                      setNicknameEditing(true)
                    }}
                    aria-label="Edit nickname"
                    title="Edit"
                  >
                    <span className="material-symbols-outlined" aria-hidden="true">edit</span>
                  </button>
                  {canShowDeviceInfoButton && (
                    <button
                      type="button"
                      className="appfactory-device-nickname-info-btn"
                      onClick={() => setDeviceInfoOpen(true)}
                      aria-label="Open device info"
                      title="Info"
                    >
                      <span className="material-symbols-outlined" aria-hidden="true">info</span>
                    </button>
                  )}
                </div>
              </div>
            )}
            {nicknameEditing && (
              <div ref={nicknameEditRootRef} className="appfactory-device-nickname-edit">
                <input
                  className="appfactory-device-nickname-input"
                  value={nicknameInput}
                  onChange={(event) => {
                    setNicknameInput(event.target.value)
                    if (nicknameError) setNicknameError(null)
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault()
                      void handleNicknameSave()
                    } else if (event.key === 'Escape') {
                      event.preventDefault()
                      cancelNicknameEditing()
                    }
                  }}
                  disabled={nicknameSaving}
                  autoFocus
                />
                <button
                  type="button"
                  className="appfactory-device-nickname-cancel-btn"
                  onClick={cancelNicknameEditing}
                  disabled={nicknameSaving}
                  aria-label="Cancel nickname edit"
                  title="Cancel"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="appfactory-device-nickname-save-btn"
                  onClick={() => {
                    void handleNicknameSave()
                  }}
                  disabled={nicknameSaving || !String(nicknameInput || '').trim()}
                  aria-label="Save nickname"
                  title="Save"
                >
                  {nicknameSaving ? 'Saving...' : 'Save'}
                </button>
              </div>
            )}
            {nicknameError && <div className="appfactory-device-nickname-error">{nicknameError}</div>}
          </div>
        )}
      </div>
      {canShowDeviceInfoButton && deviceInfoOpen && (
        <div
          className="appfactory-device-info-modal-overlay"
          role="presentation"
          onClick={() => setDeviceInfoOpen(false)}
        >
          <div
            className="appfactory-device-info-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Device info"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="appfactory-device-info-modal__header">
              <h3 className="appfactory-device-info-modal__title">Device Info</h3>
              <button
                type="button"
                className="appfactory-device-info-modal__close"
                aria-label="Close device info"
                title="Close"
                onClick={() => setDeviceInfoOpen(false)}
              >
                <span className="material-symbols-rounded">close</span>
              </button>
            </div>
            <div className="appfactory-device-info-modal__list">
              {deviceInfoRows.map((row) => (
                <div
                  key={row.key}
                  className={`appfactory-device-info-modal__row appfactory-device-info-modal__row--${row.key}`}
                >
                  <span className="appfactory-device-info-modal__label">{row.label}</span>
                  <div className="appfactory-device-info-modal__value-wrap">
                    <span
                      className={`appfactory-device-info-modal__value ${
                        row.isNA ? 'appfactory-device-info-modal__value--na' : ''
                      }`}
                    >
                      {row.value}
                    </span>
                    {row.key === 'firmwareVersion' && !row.isNA && (
                      <button
                        type="button"
                        className={`appfactory-device-info-modal__action-btn appfactory-device-info-modal__action-btn--firmware ${
                          firmwareChecking ? 'is-loading' : ''
                        }`}
                        onClick={() => {
                          void handleCheckFirmware()
                        }}
                        disabled={!canCheckFirmware || firmwareChecking}
                        aria-label={firmwareChecking ? 'Checking firmware' : 'Check new firmware'}
                        title={firmwareChecking ? 'Checking...' : 'Check new firmware'}
                      >
                        <span className="material-symbols-rounded">sync</span>
                      </button>
                    )}
                    {row.key === 'deviceSN' && !row.isNA && (
                      <button
                        type="button"
                        className={`appfactory-device-info-modal__copy-btn ${
                          deviceSnCopied ? 'is-copied' : ''
                        }`}
                        onClick={() => {
                          void handleCopyDeviceSn(row.value)
                        }}
                        aria-label={deviceSnCopied ? 'Copied device SN' : 'Copy device SN'}
                        title={deviceSnCopied ? 'Copied' : 'Copy'}
                      >
                        <span className="material-symbols-rounded">
                          {deviceSnCopied ? 'check' : 'content_copy'}
                        </span>
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {firmwareToast && (
              <div className={`appfactory-device-info-modal__toast ${firmwareToast.type}`}>
                <span className="material-symbols-rounded">
                  {firmwareToast.type === 'success'
                    ? 'check_circle'
                    : firmwareToast.type === 'error'
                      ? 'error'
                      : 'info'}
                </span>
                <span>{firmwareToast.message}</span>
              </div>
            )}
          </div>
        </div>
      )}
      <div className="smart-home-device-modal-controls appfactory-device-modal__controls">
        {controlItems.map((item, idx) => {
          const spacing = idx < controlItems.length - 1 ? getItemSpacing(item.itemKey) : 0
          const framePreset = getItemFramePreset(item.itemKey)
          return (
            <div
              key={item.itemKey}
              className={`appfactory-device-modal__item appfactory-device-modal__item--${item.type}`}
              data-item-key={item.itemKey}
              data-item-kind={item.type}
              data-item-id={item.key}
              data-item-style-id={item.itemStyleId}
              data-frame-preset={framePreset}
              style={{ marginBottom: spacing }}
              onContextMenu={(event) => {
                if (!onPreviewItemContextMenu) return
                const target = event.target as HTMLElement | null
                const currentItem = event.currentTarget as HTMLElement
                const explicitSlotElement = target?.closest('[data-css-slot]') as HTMLElement | null
                const slotElement =
                  explicitSlotElement && currentItem.contains(explicitSlotElement)
                    ? explicitSlotElement
                    : null
                let slotKey = slotElement ? String(slotElement.dataset.cssSlot || '').trim() : ''
                let fallbackSlotLabel: string | undefined
                if (!slotKey) {
                  const materialIconTarget = target?.closest('.material-symbols-rounded, .material-symbols-outlined')
                  const svgTarget = target?.closest('svg, path, circle, rect, line, polyline, polygon')
                  const controlTarget = target?.closest(
                    'button, [role="button"], [role="switch"], [role="slider"], input, select, textarea'
                  )
                  const textTarget = target?.closest(
                    'span, p, label, h1, h2, h3, h4, h5, h6, strong, small, em'
                  )
                  if (materialIconTarget) {
                    slotKey = 'materialIcon'
                    fallbackSlotLabel = 'Material Icon'
                  } else if (svgTarget) {
                    slotKey = 'svgIcon'
                    fallbackSlotLabel = 'SVG Icon'
                  } else if (controlTarget) {
                    slotKey = 'controlSurface'
                    fallbackSlotLabel = 'Control Surface'
                  } else if (textTarget) {
                    slotKey = 'text'
                    fallbackSlotLabel = 'Text'
                  } else {
                    // 無法判斷具體 slot 時，至少回退到 itemRoot，避免「元件完全沒有 CSS Editor」。
                    slotKey = 'itemRoot'
                    fallbackSlotLabel = 'Item Surface'
                  }
                }
                if (!slotKey) return
                const stateElement =
                  (target?.closest('[data-css-state]') as HTMLElement | null) ??
                  (slotElement?.closest('[data-css-state]') as HTMLElement | null)
                event.preventDefault()
                event.stopPropagation()
                onPreviewItemContextMenu({
                  itemKey: item.itemKey,
                  itemKind: item.type,
                  itemId: item.key,
                  itemStyleId: item.itemStyleId,
                  slotKey,
                  slotLabel: slotElement ? slotElement.dataset.cssSlotLabel : fallbackSlotLabel,
                  slotState: stateElement?.dataset.cssState,
                  clientX: event.clientX,
                  clientY: event.clientY,
                })
              }}
            >
              <FrameWrapper preset={framePreset}>{item.el}</FrameWrapper>
            </div>
          )
        })}
      </div>
      {showFooterActions && (
        <div className="smart-home-automation-actions">
          <button
            type="button"
            className="smart-home-automation-btn smart-home-automation-cancel"
            onClick={onClose}
          >
            取消
          </button>
          <button
            type="button"
            className="smart-home-automation-btn smart-home-automation-primary"
            onClick={onClose}
          >
            完成
          </button>
        </div>
      )}
    </div>
  )

  if (wrapMode === 'overlay') {
    return (
      <div className="smart-home-device-modal-overlay">
        {modalInner}
      </div>
    )
  }

  return (
    <div className="device-type-modal-preview-viewport-402">
      {modalInner}
    </div>
  )
}
